import React, { useEffect, useMemo, useRef, useState } from 'react'
import Icon from '../../../ui/Icon/Icon'
import DropdownBox from '../../../ui/DropdownBox/DropdownBox'

const MAX_RESULTS = 20

function RelatedBrandsMultiSelect({
  brands,
  isLoading,
  value = [],
  onChange,
  loadError,
  canBrowseOnFocus = false,
  label = 'Назви повʼязаних брендів',
  emptyMessage = 'Брендів не знайдено',
  removeTitle = 'Прибрати бренд',
  clearTitle = 'Очистити всі бренди',
}) {
  const [searchQuery, setSearchQuery] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const [activeOptionIndex, setActiveOptionIndex] = useState(-1)
  const fieldRef = useRef(null)
  const dropdownRef = useRef(null)
  const inputRef = useRef(null)
  const optionRefs = useRef([])

  const selectedIds = useMemo(() => new Set(value.map((brand) => brand.id)), [value])

  const trimmedQuery = searchQuery.trim()

  const searchResults = useMemo(() => {
    const available = brands.filter((brand) => !selectedIds.has(brand.id))

    if (!trimmedQuery) {
      if (!canBrowseOnFocus) return []
      return available.slice(0, MAX_RESULTS)
    }

    const query = trimmedQuery.toLowerCase()

    return available
      .filter((brand) => brand.name.toLowerCase().includes(query))
      .slice(0, MAX_RESULTS)
  }, [brands, trimmedQuery, selectedIds, canBrowseOnFocus])

  const showDropdown = isFocused && (trimmedQuery.length > 0 || canBrowseOnFocus)
  const hasFloatingLabel = value.length > 0 || trimmedQuery.length > 0

  useEffect(() => {
    setActiveOptionIndex(searchResults.length > 0 ? 0 : -1)
    optionRefs.current = []
  }, [searchResults])

  useEffect(() => {
    const activeOption = optionRefs.current[activeOptionIndex]

    if (activeOption) {
      activeOption.scrollIntoView({ block: 'nearest' })
    }
  }, [activeOptionIndex])

  const handleSelectBrand = (brand) => {
    onChange([...value, brand])
    setSearchQuery('')
    setActiveOptionIndex(-1)
    inputRef.current?.focus()
  }

  const handleOptionMouseDown = (event) => {
    event.preventDefault()
  }

  const handleRemoveBrand = (brandId) => {
    onChange(value.filter((brand) => brand.id !== brandId))
  }

  const handleFieldBlur = () => {
    window.setTimeout(() => {
      if (!fieldRef.current?.contains(document.activeElement) && !dropdownRef.current?.contains(document.activeElement)) {
        setIsFocused(false)
        setActiveOptionIndex(-1)
      }
    }, 0)
  }

  const handleInputKeyDown = (event) => {
    if (!showDropdown || searchResults.length === 0) {
      return
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveOptionIndex((index) => (index < searchResults.length - 1 ? index + 1 : 0))
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveOptionIndex((index) => (index > 0 ? index - 1 : searchResults.length - 1))
      return
    }

    if (event.key === 'Enter') {
      event.preventDefault()
      const brand = searchResults[activeOptionIndex]

      if (brand) {
        handleSelectBrand(brand)
      }
    }
  }

  const handleClearAll = (event) => {
    event.stopPropagation()
    onChange([])
    setSearchQuery('')
    inputRef.current?.focus()
  }

  return (
    <div className="UtmBuilder-RelatedBrandsField">
      <div
        className={[
          'Field',
          'UtmBuilder-RelatedBrandsControl',
          hasFloatingLabel ? 'Field_with_value' : '',
          value.length > 0 ? 'UtmBuilder-RelatedBrandsControl_has_clear' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        ref={fieldRef}
        onClick={() => inputRef.current?.focus()}
      >
        <div className="UtmBuilder-RelatedBrandsChips">
          {value.map((brand) => (
            <span key={brand.id} className="UtmBuilder-RelatedBrandsChip">
              <span className="UtmBuilder-RelatedBrandsChipLabel">{brand.name}</span>
              <button
                type="button"
                className="UtmBuilder-RelatedBrandsChipRemove"
                title={removeTitle}
                onMouseDown={handleOptionMouseDown}
                onClick={(event) => {
                  event.stopPropagation()
                  handleRemoveBrand(brand.id)
                }}
              >
                <Icon name="times" weight="regular" size={12} />
              </button>
            </span>
          ))}

          <input
            ref={inputRef}
            type="text"
            className="UtmBuilder-RelatedBrandsInput"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={handleFieldBlur}
            onKeyDown={handleInputKeyDown}
            role="combobox"
            aria-expanded={showDropdown}
            aria-autocomplete="list"
            aria-activedescendant={
              showDropdown && activeOptionIndex >= 0
                ? `related-brands-option-${searchResults[activeOptionIndex]?.id}`
                : undefined
            }
          />
        </div>

        {value.length > 0 && !isLoading ? (
          <button
            type="button"
            className="UtmBuilder-RelatedBrandsClear"
            title={clearTitle}
            onMouseDown={handleOptionMouseDown}
            onClick={handleClearAll}
          >
            <Icon name="times" weight="regular" size={12} />
          </button>
        ) : null}

        {isLoading && <span className="UtmBuilder-RelatedBrandsLoading" aria-hidden="true" />}
        <span className="Field-Bar" />
        <label>{label}</label>
      </div>

      {loadError && <p className="UtmBuilder-RelatedBrandsHint UtmBuilder-RelatedBrandsHint_error">{loadError}</p>}

      {showDropdown && (
        <DropdownBox ref={dropdownRef} styles={{ top: '43px' }}>
          {searchResults.length === 0 ? (
            <p className="EmptyMessage">{emptyMessage}</p>
          ) : (
            searchResults.map((brand, index) => (
              <li
                key={brand.id}
                ref={(element) => {
                  optionRefs.current[index] = element
                }}
                id={`related-brands-option-${brand.id}`}
                role="option"
                aria-selected={index === activeOptionIndex}
                className={[
                  'DropdownBox-Element',
                  index === activeOptionIndex ? 'DropdownBox-Element_theme_active' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onMouseDown={handleOptionMouseDown}
                onMouseEnter={() => setActiveOptionIndex(index)}
                onClick={() => handleSelectBrand(brand)}
              >
                <div className="SelectOption-Element">{brand.name}</div>
              </li>
            ))
          )}
        </DropdownBox>
      )}
    </div>
  )
}

export default RelatedBrandsMultiSelect
